import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Calendar, Ship } from 'lucide-react';
import { useLanguage, LanguageSwitcher } from '@/components/LanguageContext';

const Article2 = () => {
  const { t } = useLanguage();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/">
            <Button variant="outline" className="mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('common.backToHome')}
            </Button>
          </Link>
          
          <div className="flex items-center mb-4">
            <Badge className="bg-blue-600 text-white mr-4">
              <Ship className="h-3 w-3 mr-1" />
              {t('category.shipping')}
            </Badge>
            <span className="text-sm text-gray-600">Kuehne+Nagel</span>
          </div>
          
          <div className="flex items-center text-sm text-muted-foreground mb-6">
            <Calendar className="h-4 w-4 mr-1" />
            January 8, 2026
            <span className="mx-2">•</span>
            <span>6 min read</span>
          </div>
          
          <div className="flex justify-end mb-4">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Article Content */}
        <article className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            {t('article.2.title')}
          </h1>
          
          <img 
            src="https://images.unsplash.com/photo-1628097045676-97212b505372?w=1200&auto=format&fit=crop&q=80" 
            alt="Container shipping operations"
            className="w-full h-64 object-cover rounded-lg mb-8"
          />

          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 mb-6 font-medium">
              {t('article.2.intro')}
            </p>
            
            <p className="mb-6">
              {t('article.2.detail1')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.2.section1.title')}</h2>
            
            <p className="mb-6">
              {t('article.2.section1.content')}
            </p>

            <p className="mb-6">
              {t('article.2.detail2')}
            </p>

            <p className="mb-6">
              {t('article.2.detail3')}
            </p>

            <blockquote className="border-l-4 border-blue-500 pl-6 my-8 italic text-gray-700">
              "{t('article.2.quote')}"
              <footer className="text-sm text-gray-600 mt-2">
                — Michael Aldwell, Executive Vice President of Sea Logistics, Kuehne+Nagel
              </footer>
            </blockquote>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.2.section2.title')}</h2>
            
            <p className="mb-6">
              {t('article.2.section2.content')}
            </p>

            <p className="mb-6">
              {t('article.2.detail7')}
            </p>

            <p className="mb-6">
              {t('article.2.detail8')}
            </p>

            <p className="mb-6">
              {t('article.2.detail9')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.2.section3.title')}</h2>
            
            <p className="mb-6">
              {t('article.2.detail10')}
            </p>

            <p className="mb-6">
              {t('article.2.detail11')}
            </p>

            <div className="bg-blue-50 p-6 rounded-lg mt-8">
              <h3 className="font-bold text-gray-900 mb-2">Key Market Indicators</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li><strong>Asia-Europe Capacity:</strong> 1.15 million TEUs in January</li>
                <li><strong>Capacity vs Pre-COVID:</strong> Nearly 50% above normal levels</li>
                <li><strong>Blank Sailings:</strong> Almost none on Asia-Europe routes</li>
                <li><strong>US West Coast:</strong> Retained market share gains from 2025</li>
                <li><strong>Consumer Confidence:</strong> Softening in US markets</li>
              </ul>
            </div>
          </div>

          {/* Source Attribution */}
          <div className="mt-12 p-6 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">
              {t('common.sourceAttribution')} Kuehne+Nagel official newsletter.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              {t('common.sourceDetails')}
            </p>
            <a 
              href="https://mykn.kuehne-nagel.com/news/article/prechinese-new-year-shipping-diverging" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                {t('common.readOriginalArticle')}
              </Button>
            </a>
          </div>

          {/* Navigation */}
          <div className="mt-8 flex justify-between">
            <Link to="/">
              <Button variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('common.backToAllArticles')}
              </Button>
            </Link>
          </div>
        </article>
      </div>
    </div>
  );
};

export default Article2;